import React, { useState, useEffect } from 'react';
import { X, Loader2, Check, AlertCircle, FileText, Image } from 'lucide-react';
import WhatsAppIcon from '../icons/WhatsAppIcon';
import { toast } from 'react-toastify';
import { validatePhoneNumber, normalizePhoneNumber, getNetworkProvider } from '../../api/whatsapp';

// Get API URL from environment
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/**
 * PayslipWhatsAppModal Component
 * Modal for sending payslip via WhatsApp
 */
const PayslipWhatsAppModal = ({
  payslip,
  onClose,
  onSuccess,
  generateImage,
  module = 'retail'
}) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [sending, setSending] = useState(false);
  const [validation, setValidation] = useState({ isValid: false, message: '' });
  const [networkProvider, setNetworkProvider] = useState('');
  const [format, setFormat] = useState('pdf'); // 'pdf' or 'image'

  // Initialize phone number from employee phone if available
  useEffect(() => {
    if (payslip && (payslip.employee_phone || payslip.phone)) {
      setPhoneNumber(payslip.employee_phone || payslip.phone);
    }
  }, [payslip]);

  // Validate phone number on change
  useEffect(() => {
    if (phoneNumber.trim()) {
      const result = validatePhoneNumber(phoneNumber);
      setValidation(result);

      if (result.isValid) {
        const provider = getNetworkProvider(phoneNumber);
        setNetworkProvider(provider);
      } else {
        setNetworkProvider('');
      }
    } else {
      setValidation({ isValid: false, message: '' });
      setNetworkProvider('');
    }
  }, [phoneNumber]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!phoneNumber.trim()) {
      toast.error('Please enter a phone number');
      return;
    }

    // Validate phone number
    const validationResult = validatePhoneNumber(phoneNumber);
    if (!validationResult.isValid) {
      toast.error(validationResult.message);
      return;
    }

    setSending(true);

    try {
      // Normalize phone number to international format
      const normalizedPhone = normalizePhoneNumber(phoneNumber);

      let imageBase64 = null;

      // If image format selected and generateImage callback provided, generate the image
      if (format === 'image' && generateImage) {
        try {
          toast.info('Generating image...');
          const imageBlob = await generateImage();
          // Convert blob to base64
          imageBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(imageBlob);
          });
        } catch (error) {
          console.error('Image generation error:', error);
          toast.error('Failed to generate image');
          setSending(false);
          return;
        }
      }

      // Send payslip via WhatsApp
      const response = await fetch(`${API_URL}/payroll/payslips/${payslip.id}/send-whatsapp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          phoneNumber: normalizedPhone,
          format: format,
          imageBase64: imageBase64,
          module: module
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to send payslip');
      }

      toast.success(`Payslip sent successfully via WhatsApp as ${format.toUpperCase()}`);

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error('WhatsApp send error:', error);
      toast.error(error.message || 'Failed to send payslip via WhatsApp');
    } finally {
      setSending(false);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^\d+\s-]/g, '');
    setPhoneNumber(value);
  };

  if (!payslip) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <WhatsAppIcon className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">Send Payslip via WhatsApp</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-4">
          {/* Payslip Info */}
          <div className="bg-gray-50 p-3 rounded-lg mb-4 border-2 border-emerald-600">
            <p className="text-sm text-gray-600">Payslip Number</p>
            <p className="font-semibold text-gray-900">{payslip.payslip_number}</p>
            <p className="text-sm text-gray-600 mt-2">Employee</p>
            <p className="font-medium text-gray-900">{payslip.employee_name}</p>
            <p className="text-sm text-gray-600 mt-2">Net Pay</p>
            <p className="font-semibold text-emerald-600">
              ₦
              {parseFloat(payslip.net_pay || 0).toLocaleString('en-NG', {
                minimumFractionDigits: 2
              })}
            </p>
          </div>

          {/* Format Toggle */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-900 mb-2">Document Format</label>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              <button
                type="button"
                onClick={() => setFormat('pdf')}
                disabled={sending}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                  format === 'pdf'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <FileText className="w-4 h-4" />
                <span>PDF Document</span>
              </button>
              <button
                type="button"
                onClick={() => setFormat('image')}
                disabled={sending}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-l border-gray-300 ${
                  format === 'image'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <Image className="w-4 h-4" />
                <span>Image (PNG)</span>
              </button>
            </div>
          </div>

          {/* Phone Number Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              WhatsApp Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder="e.g. +234 803 123 4567"
                disabled={sending}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 pr-10 ${
                  phoneNumber.trim()
                    ? validation.isValid
                      ? 'border-green-500 bg-green-50/10'
                      : 'border-red-500 bg-red-50/10'
                    : 'border-gray-300'
                }`}
                required
              />
              <div className="absolute right-3 top-2.5 flex items-center gap-1.5">
                {phoneNumber.trim() &&
                  (validation.isValid ? (
                    <Check className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  ))}
              </div>
            </div>
            {phoneNumber.trim() && (
              <p
                className={`text-xs mt-1 ${validation.isValid ? 'text-green-600' : 'text-red-500'}`}
              >
                {validation.message}
                {networkProvider && ` (${networkProvider})`}
              </p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={sending || (phoneNumber.trim() && !validation.isValid)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <WhatsAppIcon className="w-4 h-4 text-white fill-current" />
                <span>Send via WhatsApp</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PayslipWhatsAppModal;
